import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Receipt,
  Plane,
  Building2,
  Calendar,
  MapPin,
  Plus,
  Trash2,
  Save,
  Send,
  ArrowLeft,
  DollarSign,
  AlertTriangle,
  Link2,
  FileText,
  CreditCard,
  Eye,
  UploadCloud,
  Paperclip,
  Loader2,
  ExternalLink,
  CheckCircle2,
  RotateCcw,
  Search,
  Filter,
  CheckCircle,
  Clock,
  XCircle,
  Wallet,
  AlertCircle,
  User,
  Users
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { hasPermission } from '../../constants/permissions';
import AttachmentPreviewModal from '../../components/travel/AttachmentPreviewModal';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SETTLEMENT_TABS = [
  { id: 'all', label: 'All Trips & Settlements' },
  { id: 'completed', label: 'Completed Trips – Settlement Completed' },
  { id: 'pending', label: 'Pending Trips – Settlement Pending' },
  { id: 'draft', label: 'Draft Settlements' },
  { id: 'submitted', label: 'Submitted Settlements' },
  { id: 'pending_approval', label: 'Pending Approval' },
  { id: 'approved', label: 'Approved Settlements' },
  { id: 'rejected', label: 'Rejected / Sent Back' },
];

const TravelSettlementDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [settlements, setSettlements] = useState([]);
  const [summary, setSummary] = useState(null);

  // Selected Settlement for In-Depth Drawer / Modal
  const [selectedSettlement, setSelectedSettlement] = useState(null);
  const [showSettlementModal, setShowSettlementModal] = useState(false);

  // Attachment Preview State
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  // Inline Settlement Submission Form State
  const [settlementForm, setSettlementForm] = useState({
    actual_hotel: 0,
    actual_food: 0,
    actual_local_travel: 0,
    actual_misc: 0,
    actual_ticket: 0,
    actual_other: 0,
    employee_notes: '',
    attachments: [],
    google_drive_links: [],
  });
  const [driveInput, setDriveInput] = useState('');
  const [submittingSettlement, setSubmittingSettlement] = useState(false);

  useEffect(() => {
    fetchSettlements();
  }, [activeTab]);

  const fetchSettlements = async () => {
    try {
      setLoading(true);
      const url = `${API_URL}/travel-desk/settlements?category=${activeTab}${searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : ''}`;
      const { data } = await axios.get(url, { withCredentials: true });
      setSettlements(data.settlements || []);
      setSummary(data.summary || null);
    } catch (err) {
      toast.error('Failed to load travel settlements');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchSettlements();
  };

  const handleOpenSettlement = (record) => {
    setSelectedSettlement(record);
    const cb = record.category_breakdown || {};
    setSettlementForm({
      actual_hotel: cb.hotel || 0,
      actual_food: cb.food || 0,
      actual_local_travel: cb.local_travel || 0,
      actual_misc: cb.misc || 0,
      actual_ticket: cb.ticket || 0,
      actual_other: cb.other || 0,
      employee_notes: record.employee_notes || '',
      attachments: record.attachments || [],
      google_drive_links: record.google_drive_links || [],
    });
    setShowSettlementModal(true);
  };

  const handleAddDriveLink = () => {
    const link = (driveInput || '').trim();
    if (!link) {
      toast.error('Please enter a Google Drive URL.');
      return;
    }
    if (settlementForm.google_drive_links.includes(link)) {
      toast.info('This Google Drive link is already added.');
      return;
    }
    setSettlementForm(prev => ({
      ...prev,
      google_drive_links: [...prev.google_drive_links, link]
    }));
    setDriveInput('');
  };

  const handleRemoveDriveLink = (index) => {
    setSettlementForm(prev => ({
      ...prev,
      google_drive_links: prev.google_drive_links.filter((_, i) => i !== index)
    }));
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (uploadEvt) => {
        setSettlementForm(prev => ({
          ...prev,
          attachments: [
            ...prev.attachments,
            {
              name: file.name,
              size: file.size,
              type: file.type,
              data_url: uploadEvt.target?.result,
            }
          ]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const handleRemoveAttachment = (index) => {
    setSettlementForm(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const calculatedTotal =
    (Number(settlementForm.actual_hotel) || 0) +
    (Number(settlementForm.actual_food) || 0) +
    (Number(settlementForm.actual_local_travel) || 0) +
    (Number(settlementForm.actual_misc) || 0) +
    (Number(settlementForm.actual_ticket) || 0) +
    (Number(settlementForm.actual_other) || 0);

  const advReceivedForCalc = selectedSettlement ? Number(selectedSettlement.travel_advance_amount) || 0 : 0;
  const balancePayableCalc = Math.max(0, calculatedTotal - advReceivedForCalc);
  const balanceRecoverableCalc = Math.max(0, advReceivedForCalc - calculatedTotal);

  const handleSaveSettlementSubmit = async (isDraft = false) => {
    if (!selectedSettlement) return;
    if (!isDraft && calculatedTotal <= 0) {
      toast.error('Total actual expense must be greater than zero for submission.');
      return;
    }
    try {
      setSubmittingSettlement(true);
      const payload = {
        actual_hotel: Number(settlementForm.actual_hotel) || 0,
        actual_food: Number(settlementForm.actual_food) || 0,
        actual_local_travel: Number(settlementForm.actual_local_travel) || 0,
        actual_misc: Number(settlementForm.actual_misc) || 0,
        actual_ticket: Number(settlementForm.actual_ticket) || 0,
        actual_other: Number(settlementForm.actual_other) || 0,
        employee_notes: settlementForm.employee_notes,
        attachments: settlementForm.attachments,
        google_drive_links: settlementForm.google_drive_links,
        google_drive_link: settlementForm.google_drive_links?.[0] || '',
        status: isDraft ? 'draft' : 'submitted',
        is_draft: isDraft,
      };

      const { data } = await axios.post(
        `${API_URL}/travel-desk/requests/${selectedSettlement.request_id}/submit-settlement`,
        payload,
        { withCredentials: true }
      );
      toast.success(data?.message || (isDraft ? 'Settlement draft saved successfully!' : 'Settlement submitted successfully!'));
      setShowSettlementModal(false);
      fetchSettlements();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit settlement');
    } finally {
      setSubmittingSettlement(false);
    }
  };

  const getStatusBadge = (status, label) => {
    switch (status) {
      case 'settlement_completed':
        return <span className="px-2.5 py-1 rounded-full text-[10px] font-black bg-emerald-100 text-emerald-800 border border-emerald-200">Settlement Completed</span>;
      case 'submitted':
        return <span className="px-2.5 py-1 rounded-full text-[10px] font-black bg-blue-100 text-blue-800 border border-blue-200">Submitted (Pending Approval)</span>;
      case 'draft':
        return <span className="px-2.5 py-1 rounded-full text-[10px] font-black bg-amber-100 text-amber-800 border border-amber-200">Draft Settlement</span>;
      case 'rejected':
        return <span className="px-2.5 py-1 rounded-full text-[10px] font-black bg-rose-100 text-rose-800 border border-rose-200">Rejected / Sent Back</span>;
      case 'settlement_pending':
      default:
        return <span className="px-2.5 py-1 rounded-full text-[10px] font-black bg-slate-100 text-slate-700 border border-slate-200">Settlement Pending</span>;
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-[1700px] mx-auto font-sans" data-testid="travel-settlement-dashboard">
      {/* Top Banner */}
      <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-blue-950 text-white rounded-2xl p-6 shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-amber-500/20 text-amber-400 rounded-2xl border border-amber-500/30">
            <Receipt className="w-8 h-8" />
          </div>
          <div>
            <h1 className="text-2xl font-black tracking-tight">Outstation Travel Settlement Dashboard</h1>
            <p className="text-xs text-blue-200 mt-0.5">
              Unified travel settlement lifecycle: Review itemized travel expenses, track advance recoveries, and verify reimbursements.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/travel/request/new')}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs rounded-xl shadow-md flex items-center gap-1.5 transition"
          >
            <Plus className="w-4 h-4" /> New Travel Request
          </button>
        </div>
      </div>

      {/* Summary KPI Cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm space-y-1">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Total Trips</span>
            <span className="text-xl font-black text-slate-900 font-mono">{summary.total_trips || 0}</span>
          </div>
          <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm space-y-1">
            <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider block">Settlement Done</span>
            <span className="text-xl font-black text-emerald-600 font-mono">{summary.completed_count || 0}</span>
          </div>
          <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm space-y-1">
            <span className="text-[10px] font-bold text-amber-600 uppercase tracking-wider block">Settlement Pending</span>
            <span className="text-xl font-black text-amber-600 font-mono">{summary.pending_count || 0}</span>
          </div>
          <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm space-y-1">
            <span className="text-[10px] font-bold text-blue-600 uppercase tracking-wider block">Total Advances</span>
            <span className="text-lg font-black text-slate-900 font-mono">₹ {(summary.total_advances || 0).toLocaleString('en-IN')}</span>
          </div>
          <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm space-y-1">
            <span className="text-[10px] font-bold text-purple-600 uppercase tracking-wider block">Actual Expenses</span>
            <span className="text-lg font-black text-purple-700 font-mono">₹ {(summary.total_expenses || 0).toLocaleString('en-IN')}</span>
          </div>
          <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm space-y-1">
            <span className="text-[10px] font-bold text-teal-600 uppercase tracking-wider block">Company Payable</span>
            <span className="text-lg font-black text-teal-700 font-mono">₹ {(summary.total_payable || 0).toLocaleString('en-IN')}</span>
          </div>
          <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm space-y-1">
            <span className="text-[10px] font-bold text-rose-600 uppercase tracking-wider block">Advance Recovery</span>
            <span className="text-lg font-black text-rose-700 font-mono">₹ {(summary.total_recoverable || 0).toLocaleString('en-IN')}</span>
          </div>
        </div>
      )}

      {/* Navigation Filter Tabs & Search Bar */}
      <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm space-y-3">
        <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3">
          {/* Category Tabs */}
          <div className="flex flex-wrap items-center gap-1.5 overflow-x-auto pb-1" data-testid="settlement-category-tabs">
            {SETTLEMENT_TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'text-slate-600 bg-slate-50 hover:bg-slate-100 border border-slate-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Search Box */}
          <form onSubmit={handleSearchSubmit} className="flex items-center gap-2 min-w-[280px]">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
              <input
                type="text"
                placeholder="Search Request ID, Employee, Purpose..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:bg-white transition"
              />
            </div>
            <button
              type="submit"
              className="px-3.5 py-2 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-xl"
            >
              Filter
            </button>
          </form>
        </div>
      </div>

      {/* Main Line-Item-Wise Settlement Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden" data-testid="settlements-table-container">
        <div className="p-4 bg-slate-50 border-b border-slate-200 flex justify-between items-center text-xs">
          <span className="font-bold text-slate-800">
            Outstation Travel Records ({settlements.length})
          </span>
          <span className="text-slate-500">
            Click on any travel request to review or edit post-travel settlement details
          </span>
        </div>

        {loading ? (
          <div className="p-16 text-center text-slate-500 font-semibold flex flex-col items-center justify-center gap-2">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            <span>Loading Outstation Travel Settlements...</span>
          </div>
        ) : settlements.length === 0 ? (
          <div className="p-16 text-center text-slate-400 italic font-medium">
            No Outstation Travel Requests found in this category.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left">
              <thead className="bg-slate-100 text-slate-700 font-bold uppercase text-[10px] border-b border-slate-200 whitespace-nowrap">
                <tr>
                  <th className="p-3">Request ID</th>
                  <th className="p-3">Employee Details</th>
                  <th className="p-3">Dept &amp; Designation</th>
                  <th className="p-3">Project / Purpose</th>
                  <th className="p-3">Locations &amp; Dates</th>
                  <th className="p-3">Mode &amp; Responsibility</th>
                  <th className="p-3">Ticket Booking</th>
                  <th className="p-3 text-right">Advance Disbursed</th>
                  <th className="p-3 text-right">Actual Expenses</th>
                  <th className="p-3 text-right">Settlement Balance</th>
                  <th className="p-3 text-center">Settlement Status</th>
                  <th className="p-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {settlements.map((item) => (
                  <tr
                    key={item.request_id}
                    className="hover:bg-blue-50/40 transition cursor-pointer"
                    onClick={() => handleOpenSettlement(item)}
                  >
                    {/* 1. Request ID */}
                    <td className="p-3 font-mono font-bold text-blue-700 whitespace-nowrap">
                      {item.request_number}
                    </td>

                    {/* 2. Employee Details */}
                    <td className="p-3 whitespace-nowrap">
                      <div className="font-bold text-slate-900">{item.employee_name}</div>
                      <div className="text-[11px] text-slate-500 font-mono">{item.employee_code}</div>
                    </td>

                    {/* 3. Dept & Designation */}
                    <td className="p-3 whitespace-nowrap">
                      <div className="font-medium text-slate-800">{item.department}</div>
                      <div className="text-[11px] text-slate-500">{item.designation}</div>
                    </td>

                    {/* 4. Project / Purpose */}
                    <td className="p-3 max-w-[180px]">
                      <div className="font-bold text-slate-900 truncate">{item.project}</div>
                      <div className="text-[11px] text-slate-500 truncate">{item.purpose}</div>
                    </td>

                    {/* 5. Locations & Dates */}
                    <td className="p-3 whitespace-nowrap">
                      <div className="flex items-center gap-1 font-semibold text-slate-800">
                        <span>{item.origin_city}</span>
                        <span className="text-slate-400">→</span>
                        <span>{item.destination_city}</span>
                      </div>
                      <div className="text-[10px] text-slate-500 font-mono">
                        {item.start_date} to {item.end_date}
                      </div>
                    </td>

                    {/* 6. Mode & Responsibility */}
                    <td className="p-3 whitespace-nowrap">
                      <div className="capitalize font-bold text-slate-800">{item.travel_mode}</div>
                      <div className="text-[10px] text-slate-500">{item.booking_responsibility}</div>
                    </td>

                    {/* 7. Ticket Booking */}
                    <td className="p-3 whitespace-nowrap">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                        item.ticket_booking_details.status === 'Ticket Booked'
                          ? 'bg-emerald-100 text-emerald-800'
                          : item.ticket_booking_details.status === 'Ticket Not Booked'
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-slate-100 text-slate-600'
                      }`}>
                        {item.ticket_booking_details.status}
                      </span>
                      {item.ticket_booking_details.pnr !== 'N/A' && (
                        <div className="text-[10px] text-slate-500 font-mono mt-0.5">
                          PNR: {item.ticket_booking_details.pnr}
                        </div>
                      )}
                    </td>

                    {/* 8. Advance Disbursed */}
                    <td className="p-3 text-right font-mono font-bold text-slate-900 whitespace-nowrap">
                      ₹ {(item.travel_advance_amount || 0).toLocaleString('en-IN')}
                    </td>

                    {/* 9. Actual Expenses */}
                    <td className="p-3 text-right font-mono font-bold text-purple-700 whitespace-nowrap">
                      ₹ {(item.actual_travel_expenses || 0).toLocaleString('en-IN')}
                    </td>

                    {/* 10. Settlement Balance */}
                    <td className="p-3 text-right font-mono font-bold whitespace-nowrap">
                      {item.balance_payable > 0 ? (
                        <span className="text-teal-700 font-black">+₹ {(item.balance_payable).toLocaleString('en-IN')} (Payable)</span>
                      ) : item.balance_recoverable > 0 ? (
                        <span className="text-rose-700 font-black">-₹ {(item.balance_recoverable).toLocaleString('en-IN')} (Refund)</span>
                      ) : (
                        <span className="text-slate-500">₹ 0 (Balanced)</span>
                      )}
                    </td>

                    {/* 11. Settlement Status */}
                    <td className="p-3 text-center whitespace-nowrap">
                      {getStatusBadge(item.settlement_status, item.settlement_label)}
                    </td>

                    {/* 12. Actions */}
                    <td className="p-3 text-center whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-center gap-1.5">
                        <button
                          onClick={() => handleOpenSettlement(item)}
                          className="px-2.5 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 font-bold rounded-lg text-[11px] border border-blue-200 flex items-center gap-1"
                        >
                          <Eye className="w-3.5 h-3.5" /> Settlement
                        </button>
                        <button
                          onClick={() => navigate(`/travel/request/${item.request_id}`)}
                          className="px-2.5 py-1 bg-slate-50 hover:bg-slate-100 text-slate-700 font-bold rounded-lg text-[11px] border border-slate-200 flex items-center gap-1"
                          title="View Request Dossier"
                        >
                          <FileText className="w-3.5 h-3.5" /> Dossier
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* DETAILED POST-TRAVEL EXPENSE SETTLEMENT MODAL */}
      {showSettlementModal && selectedSettlement && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-4xl w-full p-6 shadow-2xl space-y-5 max-h-[92vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b border-slate-100 pb-4">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs font-black bg-blue-50 text-blue-700 px-3 py-1 rounded-xl border border-blue-200">
                    TRAVEL ID: {selectedSettlement.request_number}
                  </span>
                  {getStatusBadge(selectedSettlement.settlement_status, selectedSettlement.settlement_label)}
                </div>
                <h3 className="font-black text-slate-900 text-lg mt-1">
                  Post-Travel Expense Settlement — {selectedSettlement.employee_name}
                </h3>
                <p className="text-xs text-slate-500">
                  {selectedSettlement.origin_city} → {selectedSettlement.destination_city} ({selectedSettlement.start_date} to {selectedSettlement.end_date})
                </p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate(`/travel/request/${selectedSettlement.request_id}`)}
                  className="px-3.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl flex items-center gap-1"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Full Dossier
                </button>
                <button onClick={() => setShowSettlementModal(false)} className="text-slate-400 hover:text-slate-600 p-1">
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Travel Summary Strip */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs bg-slate-50 p-4 rounded-2xl border border-slate-200">
              <div>
                <span className="text-slate-400 block font-medium">Department &amp; Designation</span>
                <span className="font-bold text-slate-900">{selectedSettlement.department} — {selectedSettlement.designation}</span>
              </div>
              <div>
                <span className="text-slate-400 block font-medium">Project</span>
                <span className="font-bold text-slate-900">{selectedSettlement.project}</span>
              </div>
              <div>
                <span className="text-slate-400 block font-medium">Ticket Booking</span>
                <span className="font-bold text-slate-900">{selectedSettlement.ticket_booking_details.status} ({selectedSettlement.ticket_booking_details.carrier})</span>
              </div>
              <div>
                <span className="text-slate-400 block font-medium">Travel Advance Disbursed</span>
                <span className="font-mono font-black text-emerald-700 text-sm">₹ {(selectedSettlement.travel_advance_amount || 0).toLocaleString('en-IN')}</span>
              </div>
            </div>

            {/* Itemized Expense Breakdown Form */}
            <div className="space-y-4">
              <h4 className="font-bold text-slate-900 text-sm flex items-center gap-2 border-b border-slate-100 pb-2">
                <DollarSign className="w-4 h-4 text-blue-600" /> Category-Wise Actual Expense Figures
              </h4>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">1. Hotel &amp; Lodging (₹)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={settlementForm.actual_hotel}
                    onChange={(e) => setSettlementForm({ ...settlementForm, actual_hotel: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl font-mono text-xs font-bold"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">2. Food &amp; Meals (₹)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={settlementForm.actual_food}
                    onChange={(e) => setSettlementForm({ ...settlementForm, actual_food: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl font-mono text-xs font-bold"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">3. Local Conveyance / Cab (₹)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={settlementForm.actual_local_travel}
                    onChange={(e) => setSettlementForm({ ...settlementForm, actual_local_travel: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl font-mono text-xs font-bold"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">4. Miscellaneous / Fuel / Toll (₹)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={settlementForm.actual_misc}
                    onChange={(e) => setSettlementForm({ ...settlementForm, actual_misc: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl font-mono text-xs font-bold"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">5. Travel Tickets / Fare (₹)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={settlementForm.actual_ticket}
                    onChange={(e) => setSettlementForm({ ...settlementForm, actual_ticket: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl font-mono text-xs font-bold"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">6. Other Approved Categories (₹)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={settlementForm.actual_other}
                    onChange={(e) => setSettlementForm({ ...settlementForm, actual_other: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl font-mono text-xs font-bold"
                  />
                </div>
              </div>

              {/* Settlement Financial Balance Preview Box */}
              <div className="bg-gradient-to-br from-slate-900 to-indigo-950 text-white p-5 rounded-2xl shadow-md space-y-3">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                  <div>
                    <span className="text-slate-400 block font-semibold uppercase text-[10px]">Total Actual Expenses</span>
                    <span className="font-mono font-black text-lg text-blue-400">₹ {(calculatedTotal).toLocaleString('en-IN')}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 block font-semibold uppercase text-[10px]">Advance Disbursed</span>
                    <span className="font-mono font-black text-lg text-emerald-400">₹ {(advReceivedForCalc).toLocaleString('en-IN')}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 block font-semibold uppercase text-[10px]">Balance Payable (Company)</span>
                    <span className="font-mono font-black text-lg text-teal-400">₹ {(balancePayableCalc).toLocaleString('en-IN')}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 block font-semibold uppercase text-[10px]">Recovery Due (Employee)</span>
                    <span className="font-mono font-black text-lg text-rose-400">₹ {(balanceRecoverableCalc).toLocaleString('en-IN')}</span>
                  </div>
                </div>
              </div>

              {/* Remarks / Notes */}
              <div>
                <label className="block font-bold text-slate-700 mb-1 text-xs">Employee Settlement Notes / Remarks</label>
                <textarea
                  rows="2"
                  placeholder="Enter any additional clarifications or breakdown details..."
                  value={settlementForm.employee_notes}
                  onChange={(e) => setSettlementForm({ ...settlementForm, employee_notes: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs"
                />
              </div>

              {/* Attachments & Google Drive Links */}
              <div className="space-y-3 pt-2 border-t border-slate-100">
                <div className="flex items-center justify-between">
                  <h5 className="font-bold text-slate-800 text-xs flex items-center gap-1.5">
                    <Paperclip className="w-4 h-4 text-blue-600" /> Supporting Bills &amp; Receipts
                  </h5>
                  <label className="px-3 py-1.5 bg-blue-50 text-blue-700 font-bold text-xs rounded-xl hover:bg-blue-100 cursor-pointer flex items-center gap-1">
                    <UploadCloud className="w-3.5 h-3.5" /> Upload File Bills
                    <input
                      type="file"
                      multiple
                      accept="image/*,application/pdf"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                  </label>
                </div>

                {/* Uploaded Attachments List */}
                {settlementForm.attachments.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {settlementForm.attachments.map((att, idx) => (
                      <div key={idx} className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 px-3 py-1 rounded-xl text-xs">
                        <span className="font-medium text-slate-700 truncate max-w-[200px]">{att.name || att.filename || `Bill #${idx + 1}`}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveAttachment(idx)}
                          className="text-rose-500 hover:text-rose-700 ml-1"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Google Drive Link Input */}
                <div className="flex gap-2">
                  <input
                    type="url"
                    placeholder="Add Google Drive folder/file link (drive.google.com/...)"
                    value={driveInput}
                    onChange={(e) => setDriveInput(e.target.value)}
                    className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs"
                  />
                  <button
                    type="button"
                    onClick={handleAddDriveLink}
                    className="px-3.5 py-2 bg-slate-800 text-white font-bold rounded-xl text-xs"
                  >
                    Add Link
                  </button>
                </div>

                {settlementForm.google_drive_links.length > 0 && (
                  <div className="space-y-1">
                    {settlementForm.google_drive_links.map((lnk, idx) => (
                      <div key={idx} className="flex items-center justify-between text-xs bg-blue-50 p-2 rounded-xl text-blue-900 border border-blue-200">
                        <span className="truncate max-w-[80%] font-mono">{lnk}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveDriveLink(idx)}
                          className="text-rose-600 hover:text-rose-800 font-bold"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex justify-between items-center pt-4 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setShowSettlementModal(false)}
                className="px-4 py-2 bg-slate-100 text-slate-700 font-bold text-xs rounded-xl"
              >
                Cancel
              </button>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={submittingSettlement}
                  onClick={() => handleSaveSettlementSubmit(true)}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs rounded-xl flex items-center gap-1.5"
                >
                  <Save className="w-3.5 h-3.5" /> Save as Draft
                </button>
                <button
                  type="button"
                  disabled={submittingSettlement}
                  onClick={() => handleSaveSettlementSubmit(false)}
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold text-xs rounded-xl shadow-md flex items-center gap-1.5"
                >
                  <Send className="w-3.5 h-3.5" /> {submittingSettlement ? 'Submitting...' : 'Submit Settlement'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Attachment Preview Modal */}
      <AttachmentPreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        attachment={previewAttachment}
        title="Bill &amp; Receipt Preview"
      />
    </div>
  );
};

export default TravelSettlementDashboard;

